import type { HeroId } from "@rivalwatch/shared";
import * as mt from "./cues/match.ts";
import * as fb from "./cues/feedback.ts";
import * as ui from "./cues/ui.ts";
import { audio, type BusName, type Cue } from "./engine.ts";
import { playTheme, type ThemeMode } from "./theory.ts";

export const playUi = (cue: Cue, gain = 1, bus: BusName = "ui") => audio.play({ bus, gain, priority: 6 }, cue);

/** A hero's theme in the menus (hero select, match summary). */
export function heroTheme(hero: HeroId, mode: ThemeMode, delay = 0, bus: BusName = "ui") {
  if (mode === "tease" && !audio.throttle("tease", 0.3)) return;
  audio.play({ bus, gain: mode === "tease" ? 0.8 : 0.75, priority: 8, delay }, (o) => playTheme(o, hero, mode));
}

/** Let the player hear the category they just set a volume for. */
const PREVIEW: Record<string, () => void> = {
  master: () => playUi(ui.press),
  effects: () => playUi(fb.ko(1), 0.9, "feedback"),
  voice: () => void audio.sample({ bus: "voice", priority: 9 }, "/assets/audio/vo/unlocked.mp3"),
  music: () => playUi(mt.captured, 0.8, "music"),
};

const INTERACTIVE = "button, select, input[type=checkbox], input[type=range], a[href]";
const BACK = /^(back|close|cancel|leave lobby)$/i;

/**
 * Menu sounds for the whole app, by delegation: hovering and pressing buttons, toggles, volume
 * sliders. Mark an element `data-sound="none"` to handle it yourself, or `data-sound="back"`.
 * The first gesture anywhere also wakes the audio context.
 */
export function installUiSounds() {
  const wake = () => audio.resume();
  addEventListener("pointerdown", wake, { capture: true });
  addEventListener("keydown", wake, { capture: true });

  let hovered: Element | null = null;
  document.addEventListener("pointerover", (e) => {
    const el = (e.target as Element | null)?.closest?.(INTERACTIVE) ?? null;
    if (el === hovered) return;
    hovered = el;
    if (!el || (el as HTMLButtonElement).disabled || (el as HTMLElement).dataset.sound === "none") return;
    if (audio.throttle("hover", 0.045)) playUi(ui.hover);
  });

  document.addEventListener("click", (e) => {
    const el = (e.target as Element | null)?.closest?.("button, a[href]") as HTMLElement | null;
    if (!el || (el as HTMLButtonElement).disabled) return;
    const kind = el.dataset.sound ?? (BACK.test(el.textContent?.trim() ?? "") ? "back" : "press");
    if (kind === "none") return;
    playUi(kind === "back" ? ui.back : ui.press);
  });

  document.addEventListener("change", (e) => {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    if (el.dataset?.sound === "none") return;
    if (el instanceof HTMLInputElement && el.type === "checkbox") playUi(ui.toggle(el.checked));
    else if (el instanceof HTMLInputElement && el.type === "range") PREVIEW[el.dataset.preview ?? ""]?.();
    else if (el instanceof HTMLSelectElement) playUi(ui.press);
  });

  // Detents while dragging a slider.
  document.addEventListener("input", (e) => {
    const el = e.target as HTMLInputElement;
    if (el.type === "range" && audio.throttle("detent", 0.06)) playUi(ui.hover, 0.8);
  });
}
